//src/hooks/useModelImports.ts
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createDepartmentTemplate,
  createJobPositionTemplate,
  importDepartmentTemplates,
  importGroModel,
  importJobPositionTemplates,
  listDepartmentTemplates,
  listJobPositionTemplates,
  setDepartmentTemplateActive,
  setJobPositionTemplateActive,
  updateDepartmentTemplate,
  updateJobPositionTemplate,
} from "@/services/modelImports";

export function useDepartmentTemplates(includeInactive = false, enabled = true) {
  return useQuery({
    queryKey: ["department_templates", includeInactive],
    queryFn: () => listDepartmentTemplates(includeInactive),
    staleTime: 20_000,
    enabled,
  });
}

export function useJobPositionTemplates(includeInactive = false, enabled = true) {
  return useQuery({
    queryKey: ["job_position_templates", includeInactive],
    queryFn: () => listJobPositionTemplates(includeInactive),
    staleTime: 20_000,
    enabled,
  });
}

export function useCreateDepartmentTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => createDepartmentTemplate(name),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["department_templates"] }),
  });
}

export function useUpdateDepartmentTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => updateDepartmentTemplate(id, name),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["department_templates"] }),
  });
}

export function useSetDepartmentTemplateActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) => setDepartmentTemplateActive(id, is_active),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["department_templates"] }),
  });
}

export function useCreateJobPositionTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => createJobPositionTemplate(name),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["job_position_templates"] }),
  });
}

export function useUpdateJobPositionTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => updateJobPositionTemplate(id, name),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["job_position_templates"] }),
  });
}

export function useSetJobPositionTemplateActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) => setJobPositionTemplateActive(id, is_active),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["job_position_templates"] }),
  });
}

export function useImportDepartmentTemplates() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (templateIds: string[]) => importDepartmentTemplates(templateIds),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["departments"] });
      qc.invalidateQueries({ queryKey: ["department_templates"] });
    },
  });
}

export function useImportJobPositionTemplates() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (templateIds: string[]) => importJobPositionTemplates(templateIds),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["job_positions"] });
      qc.invalidateQueries({ queryKey: ["job_position_templates"] });
    },
  });
}

export function useImportGroModel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => importGroModel(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["risks"] });
      qc.invalidateQueries({ queryKey: ["dimensions"] });
      qc.invalidateQueries({ queryKey: ["dimension_risk_rules"] });
      qc.invalidateQueries({ queryKey: ["action_plan_models"] });
    },
  });
}
